import {
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysCostCenter } from '../queryKeys';

import { fetchCostCentersAdmin } from './fetchCostCentersAdmin';
import {
  type CostCentersAdminQueryParams,
  type CostCentersAdminResponse,
} from './types';

/**
 * Generates the query options for one page of the accounting-reference
 * directory.
 *
 * @param queryParameters - Search string, scope, id and page of the listing.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCostCentersAdminQueryOptions = <
  TData = CostCentersAdminResponse,
>(
  queryParameters: CostCentersAdminQueryParams,
  options?: Omit<
    UseQueryOptions<CostCentersAdminResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchCostCentersAdmin(queryParameters, { signal }),
    queryKey: queryKeysCostCenter.getAllAdmin(queryParameters),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for one page of the accounting-reference directory (vadmin
 * only).
 *
 * @param queryParameters - Search string, scope, id and page of the listing.
 * @param options - Additional React Query options.
 * @returns Query result containing one page of accounting references.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Directory/operation/get_accounting_references_directory_vadmin_accounting_reference_get
 */
// The options factory covers callers that hold a query client directly, so this
// hook can stand without one; it is the endpoint's entry point for components.
// fallow-ignore-next-line unused-export
export const useQueryCostCentersAdmin = <TData = CostCentersAdminResponse>(
  queryParameters: CostCentersAdminQueryParams,
  options?: Omit<
    UseQueryOptions<CostCentersAdminResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getCostCentersAdminQueryOptions<TData>(queryParameters, options));
